import type { TranslateAbleText } from '@pct-ng/i18n';
import { isNil, isObject } from '@pct-ts/typeguard';

export interface NotSavedFileUploadResponse {
  file: File;
}

export interface SuccessFileUploadResponse<T> extends NotSavedFileUploadResponse {
  id: T;
}

export interface ErrorFileUploadResponse extends NotSavedFileUploadResponse {
  message: TranslateAbleText;
}

export type FileUploadResponse<T = any> =
  | NotSavedFileUploadResponse
  | SuccessFileUploadResponse<T>
  | ErrorFileUploadResponse;

export function isNotSaveFileUploadResponse(
  response: unknown,
): response is NotSavedFileUploadResponse {
  return isObject(response) && 'file' in response && response.file instanceof File;
}

export function isSuccessFileUploadResponse<T>(
  response: unknown,
): response is SuccessFileUploadResponse<T> {
  return isNotSaveFileUploadResponse(response) && 'id' in response;
}

export function isErrorFileUploadResponse(response: unknown): response is ErrorFileUploadResponse {
  return isNotSaveFileUploadResponse(response) && 'message' in response;
}

export function isFileUploadResponseEqual<T>(
  response1: FileUploadResponse<T>,
  response2: FileUploadResponse<T>,
): boolean {
  if (response1 === response2) {
    return true;
  }

  if (isNil(response1) || isNil(response2)) {
    return false;
  }

  if (isNotSaveFileUploadResponse(response1) && isNotSaveFileUploadResponse(response2)) {
    return isFilesEqual(response1.file, response2.file);
  }

  if (isSuccessFileUploadResponse(response1) && isSuccessFileUploadResponse(response2)) {
    return response1.id === response2.id;
  }

  if (isErrorFileUploadResponse(response1) && isErrorFileUploadResponse(response2)) {
    return isFilesEqual(response1.file, response2.file) && response1.message === response2.message;
  }

  return false;
}

function isFilesEqual(file1: File, file2: File): boolean {
  return (
    file1.name === file2.name &&
    file1.size === file2.size &&
    file1.type === file2.type &&
    file1.lastModified === file2.lastModified
  );
}
